
import React from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { Zap, Image, Sparkles, Clock, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const TocRapido = () => {
  const features = [
    {
      icon: Zap,
      title: "IA Avançada",
      description: "Algoritmos de inteligência artificial treinados especificamente para fotografia imobiliária"
    },
    {
      icon: Clock,
      title: "Velocidade Extrema",
      description: "Processe centenas de fotos em segundos, não em horas"
    },
    {
      icon: Sparkles,
      title: "Qualidade Profissional",
      description: "Resultados que rivalizam com edição manual profissional"
    },
    {
      icon: Image,
      title: "Múltiplos Formatos",
      description: "Suporte para todos os formatos de imagem mais utilizados"
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "R$ 29",
      period: "/mês",
      features: [
        "100 fotos por mês",
        "Edição automática básica",
        "Correção de cores",
        "Suporte por email"
      ]
    },
    {
      name: "Profissional",
      price: "R$ 79",
      period: "/mês",
      features: [
        "500 fotos por mês",
        "Edição avançada com IA",
        "HDR automático",
        "Remoção de objetos",
        "Suporte prioritário"
      ],
      popular: true
    },
    {
      name: "Empresarial",
      price: "R$ 199",
      period: "/mês",
      features: [
        "Fotos ilimitadas",
        "Todas as funcionalidades",
        "API personalizada",
        "Treinamento da equipe",
        "Suporte 24/7"
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <TocTocHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <div className="w-20 h-20 bg-[#FF5605]/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <Zap className="h-10 w-10 text-[#FF5605]" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                Toc <span className="text-[#FF5605]">Rápido</span>
              </h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8 animate-fade-in animation-delay-200">
                Edição automática de fotos com inteligência artificial. Transforme suas imagens imobiliárias 
                em segundos com qualidade profissional.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animation-delay-400">
                <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
                  Experimente Grátis
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-8 py-4 rounded-xl text-lg">
                  Ver Demonstração
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#323031] mb-6">
                Tecnologia de <span className="text-[#FF5605]">Ponta</span>
              </h2>
              <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
                Nossa IA foi treinada com milhares de fotos imobiliárias para entregar resultados perfeitos
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="text-center p-8 rounded-3xl hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-24 bg-[#F6E5C9]/20">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#323031] mb-6">
                Como <span className="text-[#FF5605]">Funciona</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#FF5605] text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">Upload das Fotos</h3>
                <p className="text-[#5B5A59]">Faça upload das suas fotos diretamente na plataforma ou via API</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#FF5605] text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">IA Processa</h3>
                <p className="text-[#5B5A59]">Nossa inteligência artificial analisa e edita automaticamente cada imagem</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#FF5605] text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">Download Pronto</h3>
                <p className="text-[#5B5A59]">Receba suas fotos editadas em qualidade profissional em segundos</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#323031] mb-6">
                Planos para todos os <span className="text-[#FF5605]">tamanhos</span>
              </h2>
              <p className="text-xl text-[#5B5A59]">Escolha o plano ideal para seu volume de trabalho</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div key={index} className={`relative rounded-3xl p-8 border-2 transition-all duration-300 hover:scale-105 ${
                  plan.popular 
                    ? 'border-[#FF5605] bg-[#FF5605]/5 shadow-xl' 
                    : 'border-gray-200 bg-white hover:border-[#FF5605]/50'
                }`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-[#FF5605] text-white px-6 py-2 rounded-full text-sm font-semibold">
                        Mais Popular
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-[#323031] mb-2">{plan.name}</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-[#FF5605]">{plan.price}</span>
                      <span className="text-[#5B5A59] ml-1">{plan.period}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <Check className="h-5 w-5 text-[#FF5605] mr-3 flex-shrink-0" />
                        <span className="text-[#323031]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button className={`w-full rounded-xl py-3 font-semibold ${
                    plan.popular 
                      ? 'bg-[#FF5605] hover:bg-[#E14D04] text-white' 
                      : 'bg-transparent border-2 border-[#FF5605] text-[#FF5605] hover:bg-[#FF5605] hover:text-white'
                  }`}>
                    Começar Agora
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Acelere sua produtividade <span className="text-[#FF5605]">hoje</span>
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Pare de perder tempo com edição manual. Experimente o Toc Rápido gratuitamente
            </p>
            <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-12 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
              Começar Teste Grátis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </main>
      <TocTocFooter />
    </div>
  );
};

export default TocRapido;
